'use client';

import { ChevronDown, ChevronUp, Check, Copy } from 'lucide-react';
import { useState } from 'react';

import {
  factorSelectionModeClasses,
  factorSelectionModeLabel,
  formatKgCo2e,
  shortenFactorId,
} from '../lib/activity-form-helpers';
import type { ResolveFactorCandidate } from '../types';
import type { FactorSelectionMode } from '../types/activity-data';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
interface ActivityFormFactorBadgeProps {
  factor: ResolveFactorCandidate | undefined;
  mode: FactorSelectionMode | undefined;
  reason?: string | null;
  loading?: boolean;
  error?: string | null;
  disabled?: boolean;
  onOverride?: () => void;
}

export function ActivityFormFactorBadge({
  factor,
  mode,
  reason,
  loading,
  error,
  disabled,
  onOverride,
}: ActivityFormFactorBadgeProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2"
        data-testid="factor-badge-loading"
      >
        <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-sm text-muted-foreground">Resolving emission factor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2"
        data-testid="factor-badge-error"
      >
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!factor) {
    return (
      <div
        className="rounded-md border border-dashed px-3 py-2"
        data-testid="factor-badge-empty"
      >
        <p className="text-sm text-muted-foreground">
          No emission factor resolved yet. Select an activity and enter quantities.
        </p>
      </div>
    );
  }

  const handleCopyId = () => {
    if (typeof navigator === 'undefined' || !factor.id) return;
    void navigator.clipboard
      .writeText(factor.id)
      .then(() => {
        setCopiedId(true);
        setTimeout(() => { setCopiedId(false); }, 1500);
      })
      .catch(() => { setCopiedId(false); });
  };

  return (
    <div className="space-y-1" data-testid="factor-badge">
      {/* Compact single-line badge */}
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={factorSelectionModeClasses(mode)}
          data-testid="factor-badge-mode"
        >
          {factorSelectionModeLabel(mode)}
        </Badge>
        <span className="text-sm">
          {factor.name} &middot; {factor.regionName} &middot;{' '}
          {factor.year} &middot; {formatKgCo2e(factor.co2ePerUnit)}/{factor.unit}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-6 px-2 text-xs"
          onClick={() => { setExpanded(!expanded); }}
          data-testid="factor-badge-toggle-details"
        >
          {expanded ? (
            <>
              Hide <ChevronUp className="ml-1 h-3 w-3" />
            </>
          ) : (
            <>
              Details <ChevronDown className="ml-1 h-3 w-3" />
            </>
          )}
        </Button>
        {onOverride && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onOverride}
            data-testid="factor-badge-override"
          >
            Override
          </Button>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div
          className="rounded-md border bg-background p-3 text-sm"
          data-testid="factor-badge-details"
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Source:</span>
            <span>{factor.source}</span>
            <span className="text-muted-foreground">Region:</span>
            <span>{factor.regionName}</span>
            <span className="text-muted-foreground">Year:</span>
            <span>{factor.year}</span>
            <span className="text-muted-foreground">Unit:</span>
            <span>{factor.unit}</span>
            <span className="text-muted-foreground">Unit Type:</span>
            <span>{factor.unitType}</span>
            <span className="text-muted-foreground">Data Version:</span>
            <span>{factor.dataVersion}</span>
            <span className="text-muted-foreground">CO2e/unit:</span>
            <span>{formatKgCo2e(factor.co2ePerUnit)}</span>
          </div>
          {reason && (
            <p className="mt-2 text-xs text-muted-foreground">Why: {reason}</p>
          )}
          {factor.id && (
            <div className="mt-2 flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">
                ID: {shortenFactorId(factor.id)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-5 px-1 text-xs"
                onClick={handleCopyId}
                disabled={disabled}
                data-testid="factor-badge-copy-id"
              >
                {copiedId ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
